import { useRouteQueryStore } from '@/stores/route-query.store';
import { DateTime } from 'luxon';
import { ref, type Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isNil } from 'lodash';

export type RouteQueryRefOptions = {
  nullQueryValue?: string;
  isArray?: boolean;
  emptyArrayTo?: 'array' | 'null' | 'undefined';
};

const emptyArrayToValue = {
  array: [],
  null: null,
  undefined: undefined,
};

export type RouteQueryType = 'string' | 'number' | 'date' | 'boolean';

export type RouteQueryValue<T extends RouteQueryType> = {
  string: string;
  number: number;
  date: Date;
  boolean: boolean;
}[T];

type RouteQueryRefValueOf<T extends RouteQueryType> = T extends 'string'
  ? string
  : T extends 'number'
    ? number
    : T extends 'date'
      ? Date
      : T extends 'boolean'
        ? boolean
        : never;

type RouteQueryRefType<T extends RouteQueryType, V> =
  | RouteQueryRefValueOf<T>
  | Extract<V, null | undefined>;

type RouteQueryRefArrayValue<
  T extends RouteQueryType,
  O extends RouteQueryRefOptions | undefined,
> = O extends { emptyArrayTo: 'null' }
  ? RouteQueryRefValueOf<T>[] | null
  : O extends { emptyArrayTo: 'undefined' }
    ? RouteQueryRefValueOf<T>[] | undefined
    : RouteQueryRefValueOf<T>[];

type ParseResult<T extends RouteQueryType> =
  | { valid: true; value: RouteQueryValue<T> }
  | { valid: false };

const parseQueryValue = (raw: unknown): string[] => {
  if (raw === undefined || raw === null) {
    return [];
  }
  const values = Array.isArray(raw) ? raw : [raw];
  for (let i = values.length - 1; i >= 0; i--) {
    if (values[i] === '' || isNil(values[i])) {
      delete values[i];
      continue;
    }
    values[i] = String(values[i]);
  }
  return values;
};

const parseRouteQueryValue = <T extends RouteQueryType>(type: T, raw: string): ParseResult<T> => {
  switch (type) {
    case 'string':
      return { valid: true, value: raw as RouteQueryValue<T> };
    case 'number': {
      const value = Number(raw);
      if (Number.isNaN(value)) {
        return { valid: false };
      }
      return { valid: true, value: value as RouteQueryValue<T> };
    }
    case 'date': {
      if (!DateTime.fromISO(raw).isValid) {
        return { valid: false };
      }
      return { valid: true, value: new Date(raw) as RouteQueryValue<T> };
    }
    case 'boolean': {
      if (raw === 'true') {
        return { valid: true, value: true as RouteQueryValue<T> };
      }
      if (raw === 'false') {
        return { valid: true, value: false as RouteQueryValue<T> };
      }
      return { valid: false };
    }
  }
};

const serializeRouteQueryValue = <T extends RouteQueryType>(
  type: T,
  value: RouteQueryValue<T> | RouteQueryValue<T>[] | null | undefined,
  options?: RouteQueryRefOptions,
): string[] | string | undefined => {
  const values = Array.isArray(value) ? value : [value];
  const results: string[] = [];
  for (let i = values.length - 1; i >= 0; i--) {
    if (options?.nullQueryValue !== undefined && values[i] === null) {
      results.push(options?.nullQueryValue);
    }
    if (values[i] === undefined || values[i] === null) {
      continue;
    }

    switch (type) {
      case 'string':
      case 'number':
        if (String(values[i]) !== '') {
          results.push(String(values[i]));
        }
        break;
      case 'date':
        results.push((values[i] as Date).toISOString());
        break;
      case 'boolean':
        results.push(values[i] ? 'true' : 'false');
        break;
    }
  }
  return options?.isArray ? results : results.at(0);
};

const parseQueryRefValue = <T extends RouteQueryType, V>(
  type: T,
  raw: string[],
  initialValue: V,
  options?: RouteQueryRefOptions,
): RouteQueryRefType<T, V>[] => {
  const results: RouteQueryRefType<T, V>[] = [];
  raw.forEach((r) => {
    if (options?.nullQueryValue !== undefined && r === options?.nullQueryValue) {
      results.push(null as RouteQueryRefType<T, V>);
      return;
    }

    const parsed = parseRouteQueryValue(type, r);
    if (parsed.valid) {
      results.push(parsed.value as RouteQueryRefType<T, V>);
    }
  });

  if (results.length === 0) {
    if (options?.isArray) {
      return Array.isArray(initialValue) ? initialValue : [];
    }
    return [initialValue as RouteQueryRefType<T, V>];
  }

  return results;
};

export function useRouteQueryRef<
  const T extends RouteQueryType,
  const O extends RouteQueryRefOptions & { isArray: true },
>(
  name: string,
  initialValue: RouteQueryRefValueOf<T>[] | undefined,
  type: T,
  options: O,
): Ref<RouteQueryRefArrayValue<T, O>>;

export function useRouteQueryRef<
  R = unknown,
  const T extends RouteQueryType = RouteQueryType,
  V extends RouteQueryRefValueOf<T> | null | undefined = RouteQueryRefValueOf<T>,
>(
  name: string,
  initialValue: V,
  type: T,
  options?: RouteQueryRefOptions,
): Ref<[unknown] extends [R] ? RouteQueryRefType<T, V> : R>;

export function useRouteQueryRef(
  name: string,
  initialValue: unknown,
  type: RouteQueryType,
  options?: RouteQueryRefOptions,
): Ref<unknown> {
  const route = useRoute();
  const routeQueryStore = useRouteQueryStore();

  const initialRaw = parseQueryValue(route.query[name]);
  const initial = parseQueryRefValue(type, initialRaw, initialValue, options);
  const queryRef = ref(
    options?.isArray
      ? initial.length
        ? initial
        : emptyArrayToValue[options?.emptyArrayTo ?? 'array']
      : initial.at(0),
  );

  if (!initial.length) {
    void routeQueryStore.patch({ [name]: undefined });
  }

  watch(queryRef, async (value) => {
    await routeQueryStore.patch({
      [name]: serializeRouteQueryValue(
        type,
        value as
          | RouteQueryValue<RouteQueryType>
          | RouteQueryValue<RouteQueryType>[]
          | null
          | undefined,
        options,
      ),
    });
  });

  return queryRef;
}
